import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  header?: React.ReactNode;
  hideDefaultHeader?: boolean;
  children: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  description,
  header,
  hideDefaultHeader = false,
  children,
  className,
  bodyClassName,
}: DrawerProps) {
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      document.body.style.overflow = "hidden";
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.body.style.overflow = "unset";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-hidden m-0 p-0">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer: 
          - Mobile (<640px): Bottom Sheet anchored at bottom (inset-x-0 bottom-0, max-h-[90dvh], rounded-t-2xl, border-t)
          - Tablet & Desktop (>=640px): Full-height Right Slide-over (inset-y-0 right-0 top-0 bottom-0, h-full, w-[480px], rounded-none, border-l, border-t-0)
      */}
      <aside
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed z-50 flex flex-col bg-white dark:bg-zinc-950 text-foreground shadow-pop m-0",
          // Mobile: bottom sheet
          "inset-x-0 bottom-0 max-h-[90dvh] rounded-t-2xl border-t border-zinc-200 dark:border-zinc-800 pb-[max(env(safe-area-inset-bottom),1rem)]",
          // Desktop: right-side slide-over panel flush with top and bottom
          "sm:inset-x-auto sm:inset-y-0 sm:top-0 sm:bottom-0 sm:right-0 sm:h-full sm:max-h-none sm:w-[480px] sm:rounded-none sm:rounded-t-none sm:border-t-0 sm:border-l sm:border-zinc-200 dark:sm:border-zinc-800 sm:pb-0",
          className
        )}
      >
        {/* Mobile handle indicator */}
        <div className="mx-auto mt-2.5 h-1.5 w-12 rounded-full bg-zinc-300 dark:bg-zinc-700 sm:hidden shrink-0" />

        {/* Header */}
        {header ? (
          <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800">
            {header}
          </div>
        ) : !hideDefaultHeader ? (
          <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-6 py-4 shrink-0">
            <div className="min-w-0 pr-4">
              {title && (
                <h2 className="text-base font-semibold leading-tight text-foreground truncate">
                  {title}
                </h2>
              )}
              {description && (
                <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                  {description}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-zinc-900 transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : null}

        {/* Body - scrolls smoothly inside full height */}
        <div className={cn("flex-1 overflow-y-auto p-4 sm:p-6 min-h-0", bodyClassName)}>{children}</div>
      </aside>
    </div>,
    document.body
  );
}
